import type { ReactNode } from 'react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { cn } from '@/shared/lib/cn';

type KpiTone = 'blue' | 'green' | 'amber' | 'violet' | 'cyan' | 'rose';

const toneStyles: Record<KpiTone, { bg: string; icon: string }> = {
  blue: { bg: 'bg-blue-50 dark:bg-blue-950/30', icon: 'text-blue-600' },
  green: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', icon: 'text-emerald-600' },
  amber: { bg: 'bg-amber-50 dark:bg-amber-950/30', icon: 'text-amber-600' },
  violet: { bg: 'bg-violet-50 dark:bg-violet-950/30', icon: 'text-violet-600' },
  cyan: { bg: 'bg-cyan-50 dark:bg-cyan-950/30', icon: 'text-cyan-600' },
  rose: { bg: 'bg-rose-50 dark:bg-rose-950/30', icon: 'text-rose-600' },
};

interface KpiCardProps {
  title: string;
  value: string;
  icon?: ReactNode;
  tone?: KpiTone;
  hint?: string;
  className?: string;
}

export function KpiCard({ title, value, icon, tone = 'blue', hint, className }: KpiCardProps) {
  const styles = toneStyles[tone];
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium text-muted-foreground truncate">{title}</p>
            <p className="text-2xl font-bold tabular-nums text-foreground">{value}</p>
            {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
          </div>
          {icon ? (
            <span
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                styles.bg,
                styles.icon,
              )}
            >
              {icon}
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function KpiGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {children}
    </div>
  );
}
